import {
    subscribeToPendingAnnouncements,
    getAnnouncementContent,
    dismissAnnouncement,
    markCtaClicked,
} from '@/services/announcements/announcementFirestore';

/** @returns {Object} Fresh state for the announcements module. */
const state = () => ({
    /** @type {Object[]} Array of recipient docs from Firestore */
    pendingRecipients: [],
    /** @type {Object<string, Object>} Cache: announcementId -> announcement content */
    announcementCache: {},
    /** @type {Object|null} Full announcement content currently being shown */
    currentAnnouncement: null,
    /** @type {string|null} ID of the current recipient doc */
    currentRecipientId: null,
    /** @type {number} Current carousel step index */
    currentStep: 0,
    /** @type {boolean} Whether the overlay is showing */
    isVisible: false,
    /** @type {Function|null} Firestore onSnapshot cleanup function */
    _unsubscribe: null,
});

const getters = {
    /** @returns {Object|null} The content object for the current step. */
    currentStepContent: (state) => {
        if (!state.currentAnnouncement || !state.currentAnnouncement.steps) return null;
        return state.currentAnnouncement.steps[state.currentStep] || null;
    },
    /** @returns {number} Total number of steps in the current announcement. */
    totalSteps: (state) => {
        return state.currentAnnouncement?.steps?.length || 0;
    },
    /** @returns {boolean} Whether the current step is the last one. */
    isLastStep: (state, getters) => {
        return state.currentStep >= getters.totalSteps - 1;
    },
    /** @returns {boolean} Whether the current step is the first one. */
    isFirstStep: (state) => {
        return state.currentStep === 0;
    },
    /** @returns {boolean} Whether an announcement is actively displayed. */
    hasAnnouncement: (state) => {
        return state.isVisible && state.currentAnnouncement !== null;
    },
};

const mutations = {
    SET_PENDING_RECIPIENTS(state, recipients) {
        state.pendingRecipients = recipients;
    },
    SET_CURRENT_ANNOUNCEMENT(state, { announcement, recipientId }) {
        state.currentAnnouncement = announcement;
        state.currentRecipientId = recipientId;
    },
    SET_CURRENT_STEP(state, step) {
        state.currentStep = step;
    },
    SET_VISIBLE(state, visible) {
        state.isVisible = visible;
    },
    SET_UNSUBSCRIBE(state, fn) {
        state._unsubscribe = fn;
    },
    CACHE_ANNOUNCEMENT(state, { id, data }) {
        state.announcementCache = { ...state.announcementCache, [id]: data };
    },
    REMOVE_RECIPIENT(state, recipientId) {
        state.pendingRecipients = state.pendingRecipients.filter((r) => r.id !== recipientId);
    },
    CLEAR_CURRENT(state) {
        state.currentAnnouncement = null;
        state.currentRecipientId = null;
        state.currentStep = 0;
        state.isVisible = false;
    },
};

const actions = {
    /**
     * Subscribes to pending announcements for the current Firebase user.
     * Automatically shows the first available announcement.
     */
    subscribe({ commit, state, dispatch, rootState }) {
        console.log('[Announcements] subscribe() called');
        const firebaseUid = rootState.user.userId;
        console.log('[Announcements] Firebase UID:', firebaseUid);
        if (!firebaseUid) {
            console.warn('[Announcements] No Firebase UID available, skipping subscription.');
            return;
        }

        // Clean up any existing subscription
        if (state._unsubscribe) {
            state._unsubscribe();
        }

        console.log('[Announcements] Subscribing to Firestore for UID:', firebaseUid);
        const unsubscribe = subscribeToPendingAnnouncements(firebaseUid, (recipients) => {
            console.log('[Announcements] Received recipients:', recipients.length, recipients);
            commit('SET_PENDING_RECIPIENTS', recipients);

            // Auto-show the first announcement if nothing is currently displayed
            if (recipients.length > 0 && !state.isVisible) {
                console.log('[Announcements] Auto-showing first announcement');
                dispatch('showNext');
            }
        });

        commit('SET_UNSUBSCRIBE', unsubscribe);
    },

    /**
     * Shows the next pending announcement. Loads content from cache or Firestore.
     * Applies a 3-second delay before making the overlay visible.
     */
    async showNext({ commit, state }) {
        const next = state.pendingRecipients.find((r) => r.id !== state.currentRecipientId);
        if (!next) {
            commit('CLEAR_CURRENT');
            return;
        }

        // Load announcement content (from cache or Firestore)
        let announcement = state.announcementCache[next.announcementId];
        if (!announcement) {
            try {
                announcement = await getAnnouncementContent(next.announcementId);
                if (announcement) {
                    commit('CACHE_ANNOUNCEMENT', { id: next.announcementId, data: announcement });
                }
            } catch (error) {
                console.error('[Announcements] Failed to load announcement content:', error);
                return;
            }
        }

        if (!announcement || !announcement.steps || announcement.steps.length === 0) {
            console.warn('[Announcements] Announcement has no content, skipping:', next.announcementId);
            return;
        }

        commit('SET_CURRENT_ANNOUNCEMENT', { announcement, recipientId: next.id });
        commit('SET_CURRENT_STEP', 0);

        // 3-second delay before showing the overlay
        setTimeout(() => {
            commit('SET_VISIBLE', true);
        }, 3000);
    },

    /**
     * Dismisses the current announcement and advances to the next one.
     */
    async dismiss({ commit, state, dispatch }) {
        if (!state.currentRecipientId) return;

        try {
            await dismissAnnouncement(state.currentRecipientId);
        } catch (error) {
            console.error('[Announcements] Failed to dismiss announcement:', error);
        }

        commit('REMOVE_RECIPIENT', state.currentRecipientId);
        commit('CLEAR_CURRENT');
        dispatch('showNext');
    },

    /**
     * Advances to the next carousel step.
     */
    nextStep({ commit, state, getters }) {
        if (!getters.isLastStep) {
            commit('SET_CURRENT_STEP', state.currentStep + 1);
        }
    },

    /**
     * Goes back to the previous carousel step.
     */
    prevStep({ commit, state, getters }) {
        if (!getters.isFirstStep) {
            commit('SET_CURRENT_STEP', state.currentStep - 1);
        }
    },

    /**
     * Records that the user clicked the CTA link.
     * Navigation is handled by the component.
     */
    async clickCta({ state }) {
        if (!state.currentRecipientId) return;
        try {
            await markCtaClicked(state.currentRecipientId);
        } catch (error) {
            console.error('[Announcements] Failed to record CTA click:', error);
        }
    },

    /**
     * Cleans up the Firestore subscription and resets state.
     */
    cleanup({ commit, state }) {
        if (state._unsubscribe) {
            state._unsubscribe();
        }
        commit('CLEAR_CURRENT');
        commit('SET_UNSUBSCRIBE', null);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
